import type { FormDocument, FormFieldDef } from '@los-workspace/workflow-runtime';
import { formatInrAmount } from '@shared/directives/los-input-directives';
import type { MasterDataItem, OpportunityDetail } from '../models/opportunity.model';
import { uiApplicantType } from './opportunity-applicant-type.util';
import {
  findPackFormField,
  resolvePackApiBindings,
  type PackAddressFieldMap,
  type PackApiBindings,
} from '../components/workflow-pack-form/pack-form-schema.resolver';
import { coerceText } from './coerce-text';
import {
  isDirectSourceType,
  resolveOpportunityCreatorDisplayName,
} from './opportunity-direct-source.util';

export type DetailFieldConfig = {
  key: string;
  label: string;
  sourcePath: string;
  dataType?: string;
  fullWidth?: boolean;
  /** When true, read value from UI-mapped opportunityData keys (record-detail workflow). */
  uiKey?: boolean;
};

export type DetailSectionConfig = {
  key: string;
  label: string;
  fields: DetailFieldConfig[];
};

export type DetailHeaderSummaryItem = {
  key: string;
  label: string;
  sourcePath: string;
  dataType?: string;
};

export type DetailProfileFields = {
  name?: string;
  location?: string;
  leadScore?: number;
};

const DETAIL_HEADER_RULES: Array<{
  matches: (path: string) => boolean;
  key: string;
  defaultLabel: string;
  dataType?: string;
}> = [
  {
    matches: (path) => /product/i.test(path),
    key: 'product',
    defaultLabel: 'Product Name',
  },
  {
    matches: (path) => path === 'loanAmount',
    key: 'loanAmount',
    defaultLabel: 'Loan Amount Requested',
    dataType: 'currency',
  },
  {
    matches: (path) => /preferredBranch/i.test(path),
    key: 'branch',
    defaultLabel: 'Branch',
  },
  {
    matches: (path) => /sourceType/i.test(path),
    key: 'sourceType',
    defaultLabel: 'Source Type',
  },
];

export type DetailTabConfig = {
  key: string;
  label: string;
  sections: DetailSectionConfig[];
};

const FORM_SECTION_TAB: Record<string, string> = {
  'basic details': 'lead',
  'loan details': 'loan',
  'address details': 'address',
  'source details': 'source',
  'lead priority': 'source',
};

const TAB_LABELS: Record<string, string> = {
  lead: 'Lead Details',
  loan: 'Loan Details',
  address: 'Address Details',
  source: 'Source Details',
  activity: 'Activity',
};

const TAB_ORDER: readonly string[] = ['lead', 'loan', 'address', 'source', 'activity'];

/** Stable sidebar order for opportunity detail tabs (Address before Source). */
export function orderDetailTabsByCanonical<T extends { key: string }>(tabs: T[]): T[] {
  const rank = (key: string): number => {
    const index = TAB_ORDER.indexOf(key);
    return index === -1 ? TAB_ORDER.length : index;
  };
  return [...tabs].sort((a, b) => rank(a.key) - rank(b.key));
}

const LABEL_PATH_BY_VALUE_PATH: Record<string, string> = {
  productId: 'productLabel',
  schemeId: 'schemeLabel',
  sourceTypeCode: 'sourceTypeLabel',
  sourceDescriptionCode: 'sourceDescriptionLabel',
  preferredBranchId: 'preferredBranchLabel',
  genderCode: 'genderLabel',
  priorityCode: 'priorityLabel',
  loanPurposeCode: 'loanPurposeLabel',
  'addresses.0.stateCode': 'addresses.0.stateLabel',
  'addresses.0.addressTypeCode': 'addresses.0.addressTypeLabel',
};

/** When a label path is empty, fall back to the stored code/id path. */
const CODE_PATH_BY_LABEL_PATH: Record<string, string> = Object.fromEntries(
  Object.entries(LABEL_PATH_BY_VALUE_PATH).map(([codePath, labelPath]) => [labelPath, codePath]),
);

const PATH_ALIASES: Record<string, string[]> = {
  contactNumber: ['mobile', 'mobileMasked'],
  phone: ['mobile', 'mobileMasked'],
  mobile: [],
  mobileMasked: ['mobile'],
  emailID: ['email'],
  email: ['emailId'],
  applicantName: ['fullName', 'organizationName', 'name'],
  textField: ['organizationName', 'fullName', 'name'],
  name: ['fullName', 'organizationName'],
  fullName: ['organizationName', 'name'],
  organizationName: ['fullName', 'name'],
  productName: ['product', 'productLabel', 'productId'],
  dateOfBirth: ['dateOfIncorporation', 'dateOfInCorporation'],
  dateOfIncorporation: ['dateOfBirth'],
  dateOfInCorporation: ['dateOfIncorporation', 'dateOfBirth'],
  date: ['dateOfIncorporation', 'dateOfBirth', 'dateOfInCorporation'],
  existingMonthlyEMI: ['existingMonthlyEmi'],
  priorityType: ['priorityCode', 'priorityLabel'],
  remark: ['priorityRemark'],
  gender: ['genderLabel', 'genderCode'],
  product: ['productLabel', 'productId', 'productName'],
  scheme: ['schemeLabel', 'schemeId'],
  branch: ['preferredBranch', 'preferredBranchLabel', 'preferredBranchId'],
  dropdown: ['productLabel', 'productId'],
  dropdown2: ['schemeLabel', 'schemeId'],
  dropdown3: ['sourceTypeLabel', 'sourceTypeCode'],
  dropdown4: ['sourceDescriptionLabel', 'sourceDescriptionCode'],
  dropdown5: ['preferredBranchLabel', 'preferredBranchId'],
  sourceType: ['sourceTypeLabel', 'sourceTypeCode'],
  sourceDescription: ['sourceDescriptionLabel', 'sourceDescriptionCode'],
  preferredBranch: ['preferredBranchLabel', 'preferredBranchId'],
  radio2: ['priorityLabel', 'priorityCode'],
  longText: ['priorityRemark'],
  productLabel: ['productId', 'productName', 'product'],
  schemeLabel: ['schemeId'],
  sourceTypeLabel: ['sourceTypeCode'],
  sourceDescriptionLabel: ['sourceDescriptionCode'],
  preferredBranchLabel: ['preferredBranchId'],
  priorityLabel: ['priorityCode'],
  genderLabel: ['genderCode'],
  'addresses.0.addressLine1': ['addresses.0.address1'],
  'addresses.0.addressLine2': ['addresses.0.address2'],
  'addresses.0.address1': ['addresses.0.addressLine1'],
  'addresses.0.address2': ['addresses.0.addressLine2'],
  'addresses.0.stateCode': ['addresses.0.stateLabel'],
  'addresses.0.stateLabel': ['addresses.0.stateCode'],
  'addresses.0.addressTypeCode': ['addresses.0.addressTypeLabel'],
  'addresses.0.addressTypeLabel': ['addresses.0.addressTypeCode'],
};

/** Pack field codes that differ between individual and corporate basic sections. */
const FIELD_CODE_PATH_FALLBACKS: Record<string, string[]> = {
  applicantName: ['fullName', 'organizationName'],
  textField: ['organizationName', 'fullName'],
  contactNumber: ['mobile', 'mobileMasked'],
  phone: ['mobile', 'mobileMasked'],
  dateOfBirth: ['dateOfBirth', 'dateOfIncorporation', 'dateOfInCorporation'],
  date: ['dateOfIncorporation', 'dateOfBirth', 'dateOfInCorporation'],
};

function isMaskedPhoneDisplay(value: unknown): boolean {
  const text = coerceText(value).trim();
  if (!text) {
    return true;
  }
  if (/[X*]/i.test(text)) {
    return true;
  }
  const digits = text.replace(/\D/g, '');
  return digits.length > 0 && digits.length < 10;
}

/** Staff opportunity detail — prefer full mobile over vendor/list masked values. */
export function resolveOpportunityContactNumber(
  detail: Pick<OpportunityDetail, 'mobile' | 'mobileMasked'> | null | undefined,
): string {
  const candidates = [detail?.mobile, detail?.mobileMasked];
  for (const candidate of candidates) {
    const text = coerceText(candidate).trim();
    if (!text || isMaskedPhoneDisplay(text)) {
      continue;
    }
    const digits = text.replace(/\D/g, '');
    if (digits.length === 10) {
      return digits;
    }
    return text;
  }
  const fallback = coerceText(detail?.mobile ?? detail?.mobileMasked).trim();
  return fallback || '—';
}

function isPhoneDetailField(field: DetailFieldConfig): boolean {
  const token = `${field.key} ${field.sourcePath}`.toLowerCase();
  return (
    token.includes('contactnumber') ||
    token.includes('contact') ||
    token.includes('phone') ||
    token.includes('mobile')
  );
}

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PACK_SEMANTIC_API_PATHS: Record<
  Exclude<keyof PackApiBindings, 'address'>,
  string
> = {
  fullName: 'fullName',
  mobile: 'mobile',
  email: 'email',
  date: 'dateOfBirth',
  gender: 'genderCode',
  product: 'productId',
  scheme: 'schemeId',
  loanAmount: 'loanAmount',
  tenureMonths: 'tenureMonths',
  grossMonthlyIncome: 'grossMonthlyIncome',
  existingMonthlyEmi: 'existingMonthlyEmi',
  sourceType: 'sourceTypeCode',
  sourceDescription: 'sourceDescriptionCode',
  preferredBranch: 'preferredBranchId',
  priority: 'priorityCode',
  priorityRemark: 'priorityRemark',
};

const ADDRESS_SEMANTIC_API_PATHS: Record<keyof PackAddressFieldMap, string> = {
  addressLine1: 'addresses.0.addressLine1',
  addressLine2: 'addresses.0.addressLine2',
  pinCode: 'addresses.0.pinCode',
  city: 'addresses.0.city',
  district: 'addresses.0.district',
  state: 'addresses.0.stateCode',
  country: 'addresses.0.countryCode',
  addressType: 'addresses.0.addressTypeCode',
};

/**
 * Merge workflow `responseFieldMapping` with pack-form bindings so detail tabs
 * resolve the correct OpportunityDetail paths for designer field codes.
 */
export function buildDetailResponseFieldMapping(
  form: FormDocument | null | undefined,
  applicantType?: string | null,
  workflowMapping?: Record<string, string> | null,
): Record<string, string> {
  const isCorporate = uiApplicantType(applicantType) === 'corporate';
  const bindings = resolvePackApiBindings(form);
  const fromBindings: Record<string, string> = {};

  for (const [semantic, fieldCode] of Object.entries(bindings)) {
    if (semantic === 'address' || typeof fieldCode !== 'string' || !fieldCode) {
      continue;
    }
    const path = resolveSemanticApiPath(semantic as Exclude<keyof PackApiBindings, 'address'>, isCorporate);
    if (path) {
      fromBindings[fieldCode] = path;
    }
  }

  for (const [semantic, fieldCode] of Object.entries(bindings.address)) {
    if (typeof fieldCode !== 'string' || !fieldCode) {
      continue;
    }
    const path = ADDRESS_SEMANTIC_API_PATHS[semantic as keyof PackAddressFieldMap];
    if (path) {
      fromBindings[fieldCode] = path;
    }
  }

  return mergeDetailResponseFieldMapping(fromBindings, workflowMapping);
}

/** Pack bindings are the baseline; workflow view mapping overrides explicit entries. */
export function mergeDetailResponseFieldMapping(
  fromBindings: Record<string, string>,
  workflowMapping?: Record<string, string> | null,
): Record<string, string> {
  const merged = {
    ...fromBindings,
    ...sanitizeWorkflowResponseFieldMapping(workflowMapping),
  };

  return Object.fromEntries(
    Object.entries(merged).map(([fieldCode, path]) => [
      fieldCode,
      preferDetailDisplayPath(path),
    ]),
  );
}

/** Detail view prefers human labels; edit/create flows do not use this helper. */
export function preferDetailDisplayPath(path: string): string {
  return LABEL_PATH_BY_VALUE_PATH[path] ?? path;
}

/**
 * Build header summary cards from the active view-form workflow when no
 * `detail_opportunity` record-detail node exists (OPPORTUNITY v5+).
 */
export function buildDetailHeaderSummaryFromViewForm(args: {
  form: FormDocument | null | undefined;
  responseFieldMapping: Record<string, string>;
}): DetailHeaderSummaryItem[] {
  const items: DetailHeaderSummaryItem[] = [];
  const seen = new Set<string>();

  for (const [fieldCode, rawPath] of Object.entries(args.responseFieldMapping)) {
    const sourcePath = preferDetailDisplayPath(rawPath);
    const rule = DETAIL_HEADER_RULES.find((entry) => entry.matches(sourcePath));
    if (!rule || seen.has(rule.key)) {
      continue;
    }

    seen.add(rule.key);
    items.push({
      key: rule.key,
      label: resolvePackFieldLabel(args.form, fieldCode, rule.defaultLabel),
      sourcePath,
      dataType: rule.dataType,
    });
  }

  return items;
}

/** Profile card title/subtitle derived from view workflow field mapping + API detail. */
export function resolveDetailProfileFromMapping(
  detail: OpportunityDetail | null,
  responseFieldMapping: Record<string, string>,
): DetailProfileFields {
  if (!detail) {
    return {};
  }

  const nameFieldCode =
    Object.keys(responseFieldMapping).find((code) => /^(applicantName|textField)$/i.test(code)) ??
    'textField';
  const namePath = preferDetailDisplayPath(
    responseFieldMapping[nameFieldCode] ?? 'fullName',
  );
  const name = resolveDetailFieldValue(
    { key: nameFieldCode, label: 'Name', sourcePath: namePath },
    detail,
    {},
  );

  const cityPath = resolveMappedPath(responseFieldMapping, /^(city|textField4)$/i, 'addresses.0.city');
  const statePath = resolveMappedPath(
    responseFieldMapping,
    /^(state|textField6)$/i,
    'addresses.0.stateCode',
  );
  const city = resolveDetailFieldValue(
    { key: 'city', label: 'City', sourcePath: cityPath },
    detail,
    {},
  );
  const state = resolveDetailFieldValue(
    { key: 'state', label: 'State', sourcePath: preferDetailDisplayPath(statePath) },
    detail,
    {},
  );
  const locationParts = [city, state]
    .map((value) => toPrimitiveDisplayString(value).trim())
    .filter(Boolean);

  return {
    name: resolveDetailProfileName(name),
    location: locationParts.length
      ? locationParts.join(', ')
      : detail.preferredBranchLabel ?? undefined,
    leadScore: detail.opportunityScore ?? 0,
  };
}

function resolveDetailProfileName(name: unknown): string | undefined {
  if (name == null) {
    return undefined;
  }
  const text = toPrimitiveDisplayString(name);
  return text || undefined;
}

function resolvePackFieldLabel(
  form: FormDocument | null | undefined,
  fieldCode: string,
  defaultLabel: string,
): string {
  const label = findPackFormField(form, fieldCode)?.label?.trim();
  if (!label) {
    return defaultLabel;
  }
  return label;
}

function resolveMappedPath(
  mapping: Record<string, string>,
  fieldCodePattern: RegExp,
  fallbackPath: string,
): string {
  const fieldCode = Object.keys(mapping).find((code) => fieldCodePattern.test(code));
  const mappedPath = fieldCode ? mapping[fieldCode] : undefined;
  return preferDetailDisplayPath(mappedPath ?? fallbackPath);
}

function resolveSemanticApiPath(
  semantic: Exclude<keyof PackApiBindings, 'address'>,
  isCorporate: boolean,
): string | undefined {
  if (semantic === 'fullName') {
    return isCorporate ? 'organizationName' : 'fullName';
  }
  if (semantic === 'date') {
    return isCorporate ? 'dateOfIncorporation' : 'dateOfBirth';
  }
  return PACK_SEMANTIC_API_PATHS[semantic];
}

function sanitizeWorkflowResponseFieldMapping(
  mapping?: Record<string, string> | null,
): Record<string, string> {
  if (!mapping) {
    return {};
  }

  const out: Record<string, string> = {};
  for (const [fieldCode, rawPath] of Object.entries(mapping)) {
    let path = normalizeWorkflowResponsePath(rawPath);
    if (
      path === 'mobileMasked' &&
      /^(contactNumber|phone)$/i.test(fieldCode.trim())
    ) {
      path = 'mobile';
    }
    if (path) {
      out[fieldCode] = path;
    }
  }
  return out;
}

function normalizeWorkflowResponsePath(path: string): string | undefined {
  const trimmed = path.trim();
  if (!trimmed || trimmed.toLowerCase() === 'null') {
    return undefined;
  }

  return trimmed
    .replace(/addresses\.0\.address1\b/g, 'addresses.0.addressLine1')
    .replace(/addresses\.0\.address2\b/g, 'addresses.0.addressLine2')
    .replace(/\bdateOfInCorporation\b/g, 'dateOfIncorporation');
}

function mergeOrphanDetailTabsIntoCanonical(
  tabs: Map<string, DetailTabConfig>,
): void {
  const canonical = new Set<string>(TAB_ORDER);
  for (const [key, tab] of [...tabs.entries()]) {
    if (!key || canonical.has(key) || key === 'activity') {
      continue;
    }
    const lead =
      tabs.get('lead') ??
      (() => {
        const created: DetailTabConfig = {
          key: 'lead',
          label: TAB_LABELS['lead'],
          sections: [],
        };
        tabs.set('lead', created);
        return created;
      })();
    lead.sections.push(...tab.sections);
    tabs.delete(key);
  }
}

function inferDetailTabForMappingField(
  fieldCode: string,
  sourcePath: string,
): string {
  const blob = `${fieldCode} ${sourcePath}`.toLowerCase();
  if (
    /address|pincode|pin_code|city|district|state|country/.test(blob) &&
    !/source/.test(blob)
  ) {
    return 'address';
  }
  if (
    /loan|product|scheme|tenure|emi|income|currency|loanamount|grossmonthly/.test(
      blob,
    )
  ) {
    return 'loan';
  }
  if (
    /source|branch|priority|remark|preferredbranch|sourcetype|sourcedescription/.test(
      blob,
    )
  ) {
    return 'source';
  }
  return 'lead';
}

function inferDetailDataTypeFromPath(path: string): string {
  const normalized = path.toLowerCase();
  if (
    normalized.includes('loanamount') ||
    normalized.includes('income') ||
    normalized.includes('emi')
  ) {
    return 'currency';
  }
  if (normalized.includes('dateof')) {
    return 'date';
  }
  if (normalized.includes('tenure')) {
    return 'number';
  }
  return 'string';
}

/** Fallback when pack form sections cannot be derived (empty form / legacy JSON). */
export function buildDetailTabsFromResponseFieldMapping(
  form: FormDocument | null | undefined,
  responseFieldMapping: Record<string, string>,
): DetailTabConfig[] {
  const buckets: Record<string, DetailFieldConfig[]> = {
    lead: [],
    loan: [],
    address: [],
    source: [],
  };

  for (const [fieldCode, rawPath] of Object.entries(responseFieldMapping)) {
    const sourcePath = preferDetailDisplayPath(rawPath);
    if (!sourcePath) {
      continue;
    }
    const tabKey = inferDetailTabForMappingField(fieldCode, sourcePath);
    buckets[tabKey].push({
      key: fieldCode,
      label: resolvePackFieldLabel(form, fieldCode, fieldCode),
      sourcePath,
      dataType: inferDetailDataTypeFromPath(sourcePath),
      fullWidth: /remark|longtext/i.test(fieldCode),
    });
  }

  const tabs = TAB_ORDER.filter(
    (key) => key !== 'activity' && buckets[key].length > 0,
  ).map((key) => ({
    key,
    label: TAB_LABELS[key],
    sections: [
      {
        key: `${key}_details`,
        label: TAB_LABELS[key],
        fields: buckets[key],
      },
    ],
  }));

  return ensureLeadTabRemarksField(tabs);
}

export function buildDetailTabsFromFormView(args: {
  form: FormDocument | null | undefined;
  responseFieldMapping: Record<string, string>;
}): DetailTabConfig[] {
  const tabs = new Map<string, DetailTabConfig>();

  for (const section of args.form?.sections ?? []) {
    const sectionTitle = coerceText(section['title']);
    const tabKey =
      FORM_SECTION_TAB[sectionTitle.toLowerCase()] ??
      (sectionTitle.toLowerCase().replace(/\s+/g, '_') || 'lead');

    if (!tabs.has(tabKey)) {
      tabs.set(tabKey, {
        key: tabKey,
        label: TAB_LABELS[tabKey] ?? sectionTitle,
        sections: [],
      });
    }

    const fields = (section.fields ?? [])
      .map((field) => toDetailField(field, args.responseFieldMapping))
      .filter((field): field is DetailFieldConfig => !!field);

    if (!fields.length) continue;

    tabs.get(tabKey)!.sections.push({
      key: coerceText(section['id']) || tabKey,
      label: sectionTitle || TAB_LABELS[tabKey] || tabKey,
      fields,
    });
  }

  mergeOrphanDetailTabsIntoCanonical(tabs);

  const fromSections = ensureLeadTabRemarksField(
    TAB_ORDER.filter((key) => key !== 'activity' && tabs.has(key)).map(
      (key) => tabs.get(key)!,
    ),
  );
  if (!fromSections.length) {
    return buildDetailTabsFromResponseFieldMapping(
      args.form,
      args.responseFieldMapping,
    );
  }

  return finalizeDetailTabConfigs(fromSections, args);
}

export function detailTabHasSubstantiveFields(
  tab: DetailTabConfig | undefined,
): boolean {
  if (!tab) {
    return false;
  }
  return tab.sections.some((section) =>
    section.fields.some((field) => !isRemarkDetailField(field)),
  );
}

/** Fill tabs that only contain remarks (or are empty) from workflow response mapping. */
export function finalizeDetailTabConfigs(
  tabs: DetailTabConfig[],
  args: {
    form: FormDocument | null | undefined;
    responseFieldMapping: Record<string, string>;
  },
): DetailTabConfig[] {
  const mappingTabs = buildDetailTabsFromResponseFieldMapping(
    args.form,
    args.responseFieldMapping,
  );
  const byKey = new Map(tabs.map((tab) => [tab.key, tab]));

  for (const key of ['lead', 'loan', 'address', 'source'] as const) {
    if (detailTabHasSubstantiveFields(byKey.get(key))) {
      continue;
    }
    const replacement = mappingTabs.find((tab) => tab.key === key);
    if (replacement) {
      byKey.set(key, replacement);
    }
  }

  const ordered = TAB_ORDER.filter(
    (key) => key !== 'activity' && byKey.has(key),
  ).map((key) => byKey.get(key)!);

  return ensureLeadTabRemarksField(ordered);
}

export function buildDetailTabsFromRecordDetail(
  tabs: Array<{
    key: string;
    label: string;
    sections?: Array<{
      key: string;
      label: string;
      fields?: Array<{
        key: string;
        label: string;
        sourcePath: string;
        dataType?: string;
        fullWidth?: boolean;
      }>;
    }>;
  }> | undefined,
): DetailTabConfig[] {
  if (
    !tabs?.some((tab) =>
      tab.sections?.some((section) => (section.fields?.length ?? 0) > 0),
    )
  ) {
    return [];
  }

  const built = tabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
    sections: (tab.sections ?? []).map((section) => ({
      key: section.key,
      label: section.label,
      fields: (section.fields ?? []).map((field) => ({
        ...field,
        uiKey: true,
      })),
    })),
  }));
  return ensureLeadTabRemarksField(orderDetailTabsByCanonical(built));
}

export function isRemarkDetailField(field: DetailFieldConfig): boolean {
  const key = field.key.toLowerCase();
  const label = field.label.toLowerCase();
  return (
    key === 'remark' ||
    key === 'longtext' ||
    key === 'priorityremark' ||
    label.includes('remark')
  );
}

/** Lead Priority remark lives on the Source tab in pack form view — show it on Lead Details like legacy LOS. */
export function ensureLeadTabRemarksField(
  tabs: DetailTabConfig[],
): DetailTabConfig[] {
  if (!tabs.length) {
    return tabs;
  }

  const next = tabs.map((tab) => ({
    ...tab,
    sections: tab.sections.map((section) => ({
      ...section,
      fields: [...section.fields],
    })),
  }));

  let remarkTemplate: DetailFieldConfig | null = null;
  for (const tab of next) {
    if (tab.key === 'lead') {
      continue;
    }
    for (const section of tab.sections) {
      const remarkIndex = section.fields.findIndex(isRemarkDetailField);
      if (remarkIndex < 0) {
        continue;
      }
      if (!remarkTemplate) {
        remarkTemplate = section.fields[remarkIndex];
      }
      section.fields.splice(remarkIndex, 1);
    }
  }

  const leadTab = next.find((tab) => tab.key === 'lead');
  if (!leadTab) {
    return next;
  }

  const leadHasRemark = leadTab.sections.some((section) =>
    section.fields.some(isRemarkDetailField),
  );
  if (leadHasRemark) {
    return next;
  }

  const remarkField: DetailFieldConfig = {
    key: remarkTemplate?.key ?? 'remark',
    label: 'Remarks',
    sourcePath: 'remark',
    dataType: remarkTemplate?.dataType ?? 'string',
    fullWidth: true,
    uiKey: true,
  };

  const hostSection =
    leadTab.sections[leadTab.sections.length - 1] ??
    (() => {
      const created = {
        key: 'lead_details',
        label: leadTab.label,
        fields: [] as DetailFieldConfig[],
      };
      leadTab.sections.push(created);
      return created;
    })();
  hostSection.fields.push(remarkField);

  return next;
}

export function resolveDetailSidebarTabs(
  tabConfigs: DetailTabConfig[],
): Array<{ key: string; label: string }> {
  const fromConfig = tabConfigs.map((tab) => ({ key: tab.key, label: tab.label }));
  if (fromConfig.length) {
    const ordered = orderDetailTabsByCanonical(
      fromConfig.filter((tab) => tab.key !== 'activity'),
    );
    const hasActivity = fromConfig.some((tab) => tab.key === 'activity');
    if (hasActivity) {
      return orderDetailTabsByCanonical(fromConfig);
    }
    return [...ordered, { key: 'activity', label: TAB_LABELS['activity'] }];
  }
  return TAB_ORDER.map((key) => ({ key, label: TAB_LABELS[key] }));
}

export function resolveDetailFieldItems(args: {
  tab: DetailTabConfig | undefined;
  detail: OpportunityDetail | null;
  uiData: Record<string, unknown>;
}): Array<{ label: string; value: unknown; fullWidth?: boolean }> {
  if (!args.tab) return [];

  const items: Array<{ label: string; value: unknown; fullWidth?: boolean }> = [];

  for (const section of args.tab.sections) {
    for (const field of section.fields) {
      items.push({
        label: field.label,
        value: resolveDetailFieldValue(field, args.detail, args.uiData),
        fullWidth: field.fullWidth,
      });
    }
  }

  return items;
}

function collectFieldLookupPaths(
  field: DetailFieldConfig,
  options: { includeExplicitKey: boolean; keyAliasesLast: boolean },
): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  const add = (path?: string) => {
    const normalized = path?.trim();
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    ordered.push(normalized);
  };

  add(field.sourcePath);
  if (options.includeExplicitKey) {
    add(field.key);
  }
  for (const alias of PATH_ALIASES[field.sourcePath] ?? []) {
    add(alias);
  }
  if (!options.keyAliasesLast) {
    for (const alias of PATH_ALIASES[field.key] ?? []) {
      add(alias);
    }
  }
  for (const alias of FIELD_CODE_PATH_FALLBACKS[field.key] ?? []) {
    add(alias);
  }
  if (options.keyAliasesLast) {
    for (const alias of PATH_ALIASES[field.key] ?? []) {
      add(alias);
    }
  }

  return ordered;
}

const UI_DETAIL_FIELD_LOOKUP_OPTIONS = {
  includeExplicitKey: true,
  keyAliasesLast: false,
} as const;

const API_DETAIL_FIELD_LOOKUP_OPTIONS = {
  includeExplicitKey: false,
  keyAliasesLast: true,
} as const;

export function resolveDetailFieldValue(
  field: DetailFieldConfig,
  detail: OpportunityDetail | null,
  uiData: Record<string, unknown>,
): unknown {
  if (field.uiKey) {
    for (const path of collectFieldLookupPaths(field, UI_DETAIL_FIELD_LOOKUP_OPTIONS)) {
      const raw = uiData[path];
      if (isNonemptyDetailUiRawValue(raw)) {
        return formatDetailValue(field, raw);
      }
    }
    return undefined;
  }

  const resolved = resolveDetailPathValue(detail, field);
  if (resolved == null || resolved === '') {
    return undefined;
  }
  return formatDetailValue(field, resolved);
}

function resolveDetailPathValue(
  detail: OpportunityDetail | null,
  field: DetailFieldConfig,
): unknown {
  if (isPhoneDetailField(field)) {
    const phone = resolveOpportunityContactNumber(detail);
    if (phone && phone !== '—') {
      return phone;
    }
  }

  const candidatePaths = collectFieldLookupPaths(
    field,
    API_DETAIL_FIELD_LOOKUP_OPTIONS,
  );

  for (const path of candidatePaths) {
    const resolved = resolveDetailPathCandidate(detail, path);
    if (resolved != null && resolved !== '') {
      return resolved;
    }
  }

  return undefined;
}

function resolveDetailPathCandidate(
  detail: OpportunityDetail | null,
  path: string,
): unknown {
  const rawValue = readDetailPath(detail, path);
  const labeledValue = readLabeledDetailValue(detail, path, rawValue);
  if (labeledValue != null && labeledValue !== '') {
    return labeledValue;
  }

  const labelFallbackPath = CODE_PATH_BY_LABEL_PATH[path];
  if (labelFallbackPath) {
    return resolveCodeLabelFallback(detail, labelFallbackPath);
  }

  if (rawValue == null || rawValue === '') {
    return undefined;
  }
  if (typeof rawValue === 'string' && looksLikeOpaqueId(rawValue)) {
    return undefined;
  }
  return rawValue;
}

function resolveCodeLabelFallback(
  detail: OpportunityDetail | null,
  labelFallbackPath: string,
): unknown {
  const codeValue = readDetailPath(detail, labelFallbackPath);
  const codeLabel = readLabeledDetailValue(detail, labelFallbackPath, codeValue);
  if (codeLabel != null && codeLabel !== '') {
    return codeLabel;
  }
  if (codeValue == null || codeValue === '') {
    return undefined;
  }
  const codeText = toPrimitiveDisplayString(codeValue);
  return codeText && !looksLikeOpaqueId(codeText) ? codeValue : undefined;
}

function isNonemptyDetailUiRawValue(raw: unknown): boolean {
  return toPrimitiveDisplayString(raw).trim() !== '';
}

export function enrichDetailLabelsFromMasters(
  detail: OpportunityDetail,
  schemes: MasterDataItem[],
  sourceDescriptions: MasterDataItem[],
  branches: MasterDataItem[] = [],
): OpportunityDetail {
  const next: OpportunityDetail = { ...detail };

  if (!next.schemeLabel && next.schemeId) {
    next.schemeLabel = findMasterLabel(schemes, next.schemeId);
  }

  if (isDirectSourceType(next.sourceTypeCode, { sourceTypeLabel: next.sourceTypeLabel }) &&
    next.sourceDescriptionCode
  ) {
    const creatorName = resolveOpportunityCreatorDisplayName(next);
    if (creatorName) {
      next.sourceDescriptionLabel = creatorName;
    }
  }

  if (
    sourceDescriptionLabelNeedsEnrichment(next) &&
    next.sourceDescriptionCode &&
    !isDirectSourceType(next.sourceTypeCode)
  ) {
    next.sourceDescriptionLabel = findMasterLabel(
      sourceDescriptions,
      next.sourceDescriptionCode,
    );
  }

  if (!next.preferredBranchLabel && next.preferredBranchId) {
    next.preferredBranchLabel = findMasterLabel(branches, next.preferredBranchId);
  }

  return next;
}

export function needsDetailLabelEnrichment(detail: OpportunityDetail): boolean {
  return (
    (!!detail.schemeId && !detail.schemeLabel && looksLikeOpaqueId(detail.schemeId)) ||
    sourceDescriptionLabelNeedsEnrichment(detail) ||
    (!!detail.preferredBranchId &&
      !detail.preferredBranchLabel &&
      looksLikeOpaqueId(detail.preferredBranchId))
  );
}

/** True when stored source description still needs a human-readable label. */
export function sourceDescriptionLabelNeedsEnrichment(
  detail: OpportunityDetail,
): boolean {
  const code = detail.sourceDescriptionCode?.trim();
  if (!code) {
    return false;
  }
  const label = detail.sourceDescriptionLabel?.trim();
  if (!label) {
    return looksLikeOpaqueId(code);
  }
  return !isMeaningfulDisplayLabel(label, code);
}

/** Prefer human label; hide opaque UUID codes when no label is available. */
export function coalesceDisplayLabel(label?: string | null, code?: string | null): string | undefined {
  const resolvedLabel = label?.trim();
  if (resolvedLabel && isMeaningfulDisplayLabel(resolvedLabel, code?.trim())) {
    return resolvedLabel;
  }

  const resolvedCode = code?.trim();
  if (!resolvedCode) return undefined;
  if (looksLikeOpaqueId(resolvedCode)) return undefined;
  return resolvedCode;
}

function toDetailField(
  field: FormFieldDef,
  responseFieldMapping: Record<string, string>,
): DetailFieldConfig | null {
  const fieldCode = coerceText(field.fieldCode);
  if (!fieldCode) return null;

  const sourcePath = responseFieldMapping[fieldCode] ?? fieldCode;
  const fieldType = String(field.fieldType ?? field.dataType ?? '').toUpperCase();

  return {
    key: fieldCode,
    label: coerceText(field.label) || fieldCode,
    sourcePath,
    dataType: inferDetailFieldDataType(field),
    fullWidth: fieldType.includes('LONG') || fieldCode === 'longText',
  };
}

function inferDetailFieldDataType(field: FormFieldDef): string {
  const fieldType = String(field.fieldType ?? field.dataType ?? '').toUpperCase();
  const fieldCode = coerceText(field.fieldCode).toLowerCase();

  if (fieldType.includes('CURRENCY') || fieldCode.startsWith('currency')) {
    return 'currency';
  }
  if (fieldType.includes('DATE')) {
    return 'date';
  }
  if (fieldType.includes('NUMBER') || fieldCode.startsWith('number')) {
    return 'number';
  }
  return 'string';
}

function readDetailPath(detail: OpportunityDetail | null, path: string): unknown {
  if (!detail) return undefined;

  if (path === 'mobileMasked' || path === 'mobile') {
    const phone = resolveOpportunityContactNumber(detail);
    if (phone && phone !== '—') {
      return phone;
    }
  }

  const direct = getByPath(detail as unknown as Record<string, unknown>, path);
  if (direct != null && direct !== '') {
    return direct;
  }

  for (const alias of PATH_ALIASES[path] ?? []) {
    const value = getByPath(detail as unknown as Record<string, unknown>, alias);
    if (value != null && value !== '') {
      return value;
    }
  }

  return undefined;
}

function readLabeledDetailValue(
  detail: OpportunityDetail | null,
  path: string,
  rawValue: unknown,
): unknown {
  if (!detail || rawValue == null || rawValue === '') {
    return undefined;
  }

  const labelPath = LABEL_PATH_BY_VALUE_PATH[path];
  if (labelPath) {
    const label = readDetailPath(detail, labelPath);
    if (label != null && label !== '') {
      const labelText = toPrimitiveDisplayString(label);
      const codeText =
        typeof rawValue === 'string' || typeof rawValue === 'number'
          ? toPrimitiveDisplayString(rawValue)
          : undefined;
      if (labelText && isMeaningfulDisplayLabel(labelText, codeText)) {
        return label;
      }
    }
  }

  if (typeof rawValue === 'string' && looksLikeOpaqueId(rawValue)) {
    return undefined;
  }

  return rawValue;
}

function formatDetailValue(field: DetailFieldConfig, value: unknown): unknown {
  if (value == null || value === '') {
    return undefined;
  }

  const dataType = String(field.dataType ?? 'string').toLowerCase();

  if (dataType === 'currency') {
    return formatCurrencyDetailValue(value);
  }

  if (dataType === 'date') {
    return formatDateDetailValue(value);
  }

  if (dataType === 'number' && field.sourcePath.endsWith('tenureMonths')) {
    return formatTenureDetailValue(value);
  }

  if (dataType === 'string') {
    return formatStringDetailValue(field, value);
  }

  return value;
}

function formatStringDetailValue(field: DetailFieldConfig, value: unknown): unknown {
  if (
    field.sourcePath.endsWith('priorityCode') ||
    field.sourcePath.endsWith('priorityLabel')
  ) {
    return formatPriorityDetailValue(value);
  }
  if (field.sourcePath.includes('addressType')) {
    return formatAddressTypeDetailValue(value);
  }
  return value;
}

function formatCurrencyDetailValue(value: unknown): string | undefined {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return undefined;
  }
  return `₹ ${formatInrAmount(amount) || '0'}`;
}

function formatDateDetailValue(value: unknown): string | undefined {
  const text = toPrimitiveDisplayString(value);
  return text ? formatDisplayDate(text) : undefined;
}

function formatTenureDetailValue(value: unknown): string | undefined {
  const text = toPrimitiveDisplayString(value);
  return text ? `${text} Months` : undefined;
}

function formatPriorityDetailValue(value: unknown): string | undefined {
  const text = toPrimitiveDisplayString(value);
  if (!text) {
    return undefined;
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function formatAddressTypeDetailValue(value: unknown): string | undefined {
  const text = toPrimitiveDisplayString(value);
  if (!text) {
    return undefined;
  }
  return text
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function toPrimitiveDisplayString(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }
  return '';
}

function formatDisplayDate(value: string): string | undefined {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-GB');
}

function findMasterLabel(items: MasterDataItem[], value: string): string | undefined {
  const normalized = value.trim();
  const match = items.find((item) => item.code === normalized || item.id === normalized);
  return match?.label;
}

function looksLikeOpaqueId(value: string): boolean {
  return UUID_LIKE.test(value.trim());
}

function isMeaningfulDisplayLabel(label: string, code?: string): boolean {
  const trimmedLabel = label.trim();
  if (!trimmedLabel) {
    return false;
  }
  const trimmedCode = code?.trim();
  if (looksLikeOpaqueId(trimmedLabel)) {
    return Boolean(trimmedCode && trimmedLabel !== trimmedCode);
  }
  if (trimmedCode && trimmedLabel === trimmedCode && looksLikeOpaqueId(trimmedCode)) {
    return false;
  }
  return true;
}

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.replace(/^\$\.?/, '').split('.').filter(Boolean);
  let current: unknown = obj;

  for (const part of parts) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}
